import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

const dialogs: HTMLElement[] = [];
const originalInert = new Map<HTMLElement, boolean>();
let previousOverflow = "";

function containBackground() {
  const active = dialogs[dialogs.length - 1];
  for (const element of Array.from(document.body.children)) {
    if (!(element instanceof HTMLElement)) continue;
    if (!originalInert.has(element)) originalInert.set(element, element.inert);
    element.inert = active ? element !== active : originalInert.get(element) || false;
  }
  if (!active) {
    for (const [element, inert] of originalInert) element.inert = inert;
    originalInert.clear();
  }
}

interface Props {
  titleId: string;
  children: ReactNode;
  onClose: () => void;
  busy?: boolean;
  className?: string;
  layer?: number;
}

/** Shared keyboard, focus, background and scrolling contract for booking dialogs. */
export default function BookingDialog({ titleId, children, onClose, busy = false, className = "max-w-lg", layer = 50 }: Props) {
  const root = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const current = useRef({ onClose, busy });
  current.current = { onClose, busy };

  useEffect(() => {
    const container = root.current!;
    const dialog = panel.current!;
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!dialogs.length) {
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    dialogs.push(container);
    containBackground();
    const controls = () => Array.from(dialog.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])')).filter(element => !element.closest('[hidden], [inert]') && getComputedStyle(element).display !== "none" && getComputedStyle(element).visibility !== "hidden");
    const focusFirst = () => (dialog.querySelector<HTMLElement>('[data-autofocus]:not(:disabled)') || controls()[0] || dialog).focus();
    focusFirst();
    const onKey = (event: KeyboardEvent) => {
      if (dialogs[dialogs.length - 1] !== container) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (!current.current.busy) current.current.onClose();
      }
      if (event.key === "Tab") {
        const elements = controls();
        const first = elements[0], last = elements[elements.length - 1];
        if (!first) { event.preventDefault(); dialog.focus(); }
        else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) { event.preventDefault(); last!.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    const onFocus = (event: FocusEvent) => {
      if (dialogs[dialogs.length - 1] === container && !dialog.contains(event.target as Node)) focusFirst();
    };
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("focusin", onFocus);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("focusin", onFocus);
      dialogs.splice(dialogs.indexOf(container), 1);
      containBackground();
      if (!dialogs.length) document.body.style.overflow = previousOverflow;
      if (trigger?.isConnected && !trigger.closest("[inert]")) trigger.focus();
    };
  }, []);

  return createPortal(
    <div ref={root} data-booking-dialog className="fixed inset-0 flex items-center justify-center bg-sea-900/50 p-3 sm:p-4" style={{ zIndex: layer }}>
      <div ref={panel} role="dialog" aria-modal="true" aria-busy={busy} aria-labelledby={titleId} tabIndex={-1} className={`booking-dialog flex max-h-[calc(100dvh-1.5rem)] w-full min-w-0 flex-col overflow-hidden rounded-xl bg-white shadow-xl outline-none sm:max-h-[calc(100dvh-2rem)] ${className}`}>
        {children}
      </div>
    </div>, document.body,
  );
}
